import { get, set } from 'dot-prop-immutable';
import debounce from 'lodash/debounce';
import _get from 'lodash/get';
import _trim from 'lodash/trim';
import _isEmpty from 'lodash/isEmpty';
import { createRef } from 'react';
import ReactDOM from 'react-dom';

/*
 * TODO: Remove validationRunner from moet-core once
 * forms.js and all its dependent files are moved to products
 */
import validationRunner from '../validation/runner';

export const isObject = val => {
  return typeof val === 'object' && val !== null && !Array.isArray(val);
};

/**
 * Init form fields with validation etc.
 *
 * @param {Object} fields
 * @param {Object} state state (or subsection of)
 *
 * @returns {Object} fields - initialised with validation etc.
 */
export const initForm = (fields, state = {}) => {
  const initFields = {};
  const fieldsWithoutRef = ['platform', 'toc', 'email', 'country', 'website'];

  Object.keys(fields).forEach(key => {
    const field = fields[key];
    const { isValid, errorMessage } = validationRunner(field.value, field.validators, {
      state,
    });

    const ref = fieldsWithoutRef.includes(key) ? undefined : field.ref || createRef();

    initFields[key] = {
      ...field,
      isInitialized: field.isInitialized || field.isOptional || !_isEmpty(field.value),
      isValid,
      ref,
      extraProps: { ...field.extraProps, inputRef: ref },
      errorMessage,
    };
  });
  return initFields;
};

/**
 * Init (subsection of) a container state that has a fields property
 * @NOTE wrapper method around initForm above - which only inits the fields part
 *
 * @param {Object} state
 *
 * @returns {Object} the state (subsection) initialised with validation etc.
 */
export const initFormFromState = state => {
  const { fields } = state;
  return {
    ...state,
    fields: initForm(fields, state),
  };
};

export const isFormValid = fields => {
  // in stakeholder object address is a nested object
  const flattenFields = flattenFormFields(fields);

  const allFieldsAreValid = Object.values(flattenFields).every(
    ({ isValid = false } = {}) => isValid,
  );

  return allFieldsAreValid;
};

export const isFormInitialized = fields => {
  return Object.values(fields).reduce((isInitialized, field) => {
    if (isObject(field) && !Object.prototype.hasOwnProperty.call(field, 'isInitialized')) {
      return isFormInitialized(field);
    }

    return !!(isInitialized && field.isInitialized);
  }, true);
};

export const isFormInitializedAndValid = fields => {
  return isFormInitialized(fields) && isFormValid(fields);
};

export const getFieldLabel = ({
  label,
  isInitialized,
  isValid,
  errorMessage,
  optionalSuffix = null,
}) => {
  const finalLabel =
    isInitialized && !isValid
      ? errorMessage
      : `${label}${optionalSuffix !== null ? ` (${optionalSuffix})` : ''}`;
  return finalLabel;
};

/**
 * @deprecated use onBlurFields
 */
export const onBlur = (key, state, callbackFunction) => {
  const field = get(state.fields, key);
  onChange(key, field.value, state, callbackFunction);
};

/**
 * @deprecated use onChangeFields
 */
export const onChange = (key, val, state, callbackFunction) => {
  const field = get(state.fields, key);
  const { isValid, errorMessage } = validationRunner(val, field.validators, {
    state,
  });

  const updatedFields = set(state.fields, key, {
    ...field,
    value: val,
    isInitialized: true,
    isValid,
    errorMessage,
  });

  callbackFunction({
    ...state,
    fields: updatedFields,
  });
};

export const onBlurFields = (key, state, callbackFunction) => {
  const field = get(state.fields, key);
  const { isValid, errorMessage } = validationRunner(field.value, field.validators, {
    state,
  });

  const updatedFields = set(state.fields, key, {
    ...field,
    value: field.value,
    isInitialized: true,
    isValid,
    errorMessage,
  });

  callbackFunction(updatedFields);
};

const setInitialized = debounce((key, fields, callbackFunction) => {
  const updatedFields = set(fields, `${key}.isInitialized`, true);

  callbackFunction(updatedFields, key);
}, 1000);

export const onChangeFields = (mainFieldKey, val, state, callbackFunction) => {
  const mainField = get(state.fields, mainFieldKey);

  const { resetFields = [], validateFields = [] } = mainField;
  const keys = [mainFieldKey, ...resetFields, ...validateFields];

  const updatedFields = keys.reduce((allFields, currentKey) => {
    const currentField = get(state.fields, currentKey);
    let currentValue;

    const isMainFieldKey = currentKey === mainFieldKey;
    const isResetField = resetFields.includes(currentKey);
    const isValidateField = validateFields.includes(currentKey);

    if (isMainFieldKey) {
      currentValue = val;
    } else if (isResetField) {
      currentValue = '';
    } else {
      currentValue = currentField.value;
    }

    const { isValid, errorMessage } =
      isMainFieldKey || isValidateField
        ? validationRunner(currentValue, currentField.validators, {
            state,
          })
        : currentField;

    return set(allFields, currentKey, {
      ...currentField,
      value: currentValue,
      isInitialized: isResetField ? false : currentField.isInitialized,
      isValid,
      errorMessage,
    });
  }, state.fields);

  callbackFunction(updatedFields, mainFieldKey);

  [mainFieldKey, ...validateFields].forEach(key => {
    setInitialized(key, updatedFields, callbackFunction);
  });
};

const EMPTY_DROPDOWN_HIDDEN_OPTION = {
  key: '',
  label: '',
  disabled: true,
  hidden: true,
};

const EMPTY_DROPDOWN_OPTION = {
  key: '',
  label: '',
  disabled: false,
  hidden: false,
};

export const addEmptyDropdownItem = (items, { add = true } = {}) => {
  if (!items.find(o => o.key === '')) {
    // hidden one is a fix to allow selecting first options
    return [add ? EMPTY_DROPDOWN_OPTION : EMPTY_DROPDOWN_HIDDEN_OPTION, ...items];
  }
  return items;
};

const removeHyphens = value => {
  return value.split('-').join('');
};

export const sanitize = object => {
  if (typeof object === 'string') {
    return _trim(object);
  }

  if (Array.isArray(object)) {
    return object.map(sanitize);
  }

  if (!!object && typeof object === 'object') {
    return Object.entries(object).reduce(
      (accumulated, [key, value]) => ({
        ...accumulated,
        [key]: sanitize(value),
      }),
      {},
    );
  }

  return object;
};

export const getMappedValues = (fields = {}, country = null) => {
  const mappedValues = {};
  Object.keys(fields).forEach(key => {
    // Only remove the hyphens if country is US or CA, remove this once agreed with KYM that hyphens
    // are fine
    if (key === 'personalId' && ['US', 'CA'].includes(country))
      fields[key].value = removeHyphens(fields[key].value);
    mappedValues[key] = sanitize(fields[key]?.value);
  });
  return mappedValues;
};

export const getOptionKeys = (options = []) => options.map(o => o.key);

export const updateTranslatedFields = (fieldTranslationsMap, key, t) => {
  const translationTarget = fieldTranslationsMap[key];
  translationTarget.label = t(translationTarget.label);
  if (translationTarget.validators) {
    translationTarget.validators = translationTarget.validators.map(condition => {
      condition.errorMessage = t(condition.errorMessage);
      return condition;
    });
  }

  return translationTarget;
};

/**
 * Requires smoothscroll-polyfill for < IE11
 * @param element
 * @param extraProps
 */
export const centerScrollOnFocus = (element, extraProps) => {
  if (extraProps && (extraProps.autoFocus || extraProps.centerOnFocus)) {
    const hasScrollIntoView = _get(element, 'currentTarget.scrollIntoView', null);
    if (hasScrollIntoView) {
      element.currentTarget.scrollIntoView({
        behavior: 'smooth',
        block: extraProps.centerOnFocus || 'center',
      });
    }
  }
};

export const initializeFields = fields =>
  Object.keys(fields).reduce(
    (acc, curr) => ({
      ...acc,
      [curr]: {
        ...fields[curr],
        isInitialized: true,
      },
    }),
    {},
  );

export const findFirstInvalidFieldWithRef = arrayOfFields => {
  let firstInvalidField;

  arrayOfFields.find(fields => {
    const fieldKeys = Object.keys(fields);

    // Get the first invalid field
    const firstInvalidFieldIndex = fieldKeys.findIndex(fName => !fields[fName].isValid);

    if (firstInvalidFieldIndex === -1) {
      // No invalid field on the current set, go to the next set of fields
      return undefined;
    }

    // Get the key for the field
    const firstInvalidFieldKey = fieldKeys[firstInvalidFieldIndex];

    // Check if the field has a ref object
    if (fields[firstInvalidFieldKey].ref && fields[firstInvalidFieldKey].ref.current) {
      firstInvalidField = fields[firstInvalidFieldKey];
    } else {
      // If the field doesn't have the ref object we use the closest field available to scroll
      let distance;
      let closestFieldKey;

      fieldKeys.forEach((key, index) => {
        if (
          (!distance || distance > Math.abs(firstInvalidFieldIndex - index)) &&
          fields[key].ref &&
          fields[key].ref.current &&
          (!fields[key].extraProps || !fields[key].extraProps?.disabled)
        ) {
          distance = firstInvalidFieldIndex - index;
          closestFieldKey = key;
        }
      });

      firstInvalidField = fields[closestFieldKey];
    }

    // We just return true here to stop the find loop
    if (firstInvalidField) return true;
  });

  return firstInvalidField;
};

export const callToActionOnNextWrapper = (fields, onNext, customValidation = undefined) => {
  // On stakeholder forms, this is a array of fields object
  // so we just turn into an array to simplify things
  const safeArrayOfFields = Array.isArray(fields) ? fields : [fields];

  // If the customValidation is set to true or all fields are valid, we call onNext
  if (
    customValidation === true ||
    (customValidation === undefined && safeArrayOfFields.every(isFormValid))
  ) {
    return onNext();
  } else {
    const firstInvalidField = findFirstInvalidFieldWithRef(safeArrayOfFields);

    // If we are able to get a valid ref, this is not undefined/null
    if (firstInvalidField) {
      // We need to use findDomNode here because we don't have control over bubble-ui
      // and bubble-ui doesn't pass the ref to the final "input" field
      // eslint-disable-next-line react/no-find-dom-node
      const domElement = ReactDOM.findDOMNode(firstInvalidField.ref.current);

      // If the found dom element is already an <input> field, we use it directly to focus on.
      // Otherwise we need to find the including child <input> or <select> element first.
      const inputElement =
        domElement.nodeName === 'INPUT' ? domElement : domElement.querySelector('input, select');

      if (inputElement && inputElement.focus) {
        inputElement.focus();
      }

      domElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
};

/**
 * Flatten form with nested fields.
 *
 * @param {Object} fields
 *
 * @returns {Object} flattened form fields
 */
export const flattenFormFields = obj => {
  if (!isObject(obj)) {
    return obj;
  }
  return Object.entries(obj).reduce((flattenedObject, [key, value]) => {
    if (isObject(value) && !Object.prototype.hasOwnProperty.call(value, 'isValid')) {
      flattenedObject = { ...flattenedObject, ...flattenFormFields(value) };
    } else {
      flattenedObject[key] = value;
    }
    return flattenedObject;
  }, {});
};
